'use client';

/* ──────────────────────────────────────────────
 *  TunnelFooterBar — barre d'action du bas en colonne
 *  unique, partagée par les tunnels réservation, carte
 *  cadeau et course. Purement présentationnelle (aucun
 *  contexte) : chaque tunnel l'alimente via un adaptateur
 *  (`StepFooter` pour la réservation).
 *  Sticky en bas de `.bt-root` (jamais `fixed` : bornée
 *  par la boîte du widget, y compris en embed étroit),
 *  masquée en layout `wide` où l'action vit dans la
 *  sidebar récap.
 *
 *  Le bloc total/compteur déplie une feuille juste
 *  au-dessus de la barre. La feuille est un enfant de la
 *  barre et jamais un portal : elle reste bornée à la
 *  boîte du widget. Fermeture : bouton, Échap, clic
 *  dehors, glissement vers le bas, changement d'étape
 *  ou de phase (ouverture du paiement, cf. `stepKey`).
 * ────────────────────────────────────────────── */

import { useCallback, useEffect, useId, useRef, useState, type JSX, type ReactNode, type TouchEvent } from 'react';

/** Glissement vertical minimal (px) qui referme la feuille. */
export const SHEET_SWIPE_CLOSE_THRESHOLD_PX = 60;

export interface TunnelFooterBarAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Affiche un spinner à la place du label (soumission en cours). */
  loading?: boolean;
}

export interface TunnelFooterBarLabels {
  back: string;
  close: string;
  show_detail: string;
  hide_detail: string;
  item_singular: string;
  item_plural: string;
}

export interface TunnelFooterBarProps {
  /** Retour à l'étape précédente (absent : pas de bouton, ex. première étape). */
  onBack?: () => void;
  /** Action principale (absente : l'étape porte la sienne, ex. paiement Stripe). */
  primary?: TunnelFooterBarAction;
  /** Total déjà résolu et formaté (peut être '' ou '…' pendant le chargement). */
  totalValue: string;
  /** Total au prix normal, barré devant `totalValue` quand une remise s'applique. */
  totalStruck?: string;
  /** Nombre d'éléments du récap : à 0, ni total ni détail (l'action reste à droite). */
  count: number;
  /** Titre de la feuille dépliée. */
  sheetTitle: string;
  /** Contenu de la feuille (en pratique `TunnelSummary variant="sheet"`), monté à l'ouverture seulement. */
  sheet: ReactNode;
  /** Identifiant de l'étape affichée, ou de sa phase (paiement ouvert) : la feuille se referme quand il change. */
  stepKey?: string;
  labels: TunnelFooterBarLabels;
}

export function TunnelFooterBar({
  onBack,
  primary,
  totalValue,
  totalStruck,
  count,
  sheetTitle,
  sheet,
  stepKey,
  labels,
}: TunnelFooterBarProps): JSX.Element {
  const sheetId = `bt-footer-sheet-${useId()}`;
  const footerRef = useRef<HTMLDivElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const closeDetail = useCallback((): void => setDetailOpen(false), []);

  /* Dernière ligne retirée depuis la feuille : plus rien à déplier. */
  useEffect((): void => {
    if (count === 0) setDetailOpen(false);
  }, [count]);

  /* Le détail se referme au changement d'étape (l'action reprend la main). */
  useEffect((): void => {
    setDetailOpen(false);
  }, [stepKey]);

  useEffect((): (() => void) | undefined => {
    if (!detailOpen) return undefined;

    /* `composedPath()` et non `event.target` : côté widget le tunnel vit dans un
       Shadow DOM, où la cible d'un listener au niveau `document` est retargetée
       sur l'élément hôte — tout clic interne passerait alors pour un clic dehors. */
    const onPointerDown = (event: MouseEvent): void => {
      const footer = footerRef.current;
      if (footer && !event.composedPath().includes(footer)) setDetailOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setDetailOpen(false);
      toggleRef.current?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return (): void => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [detailOpen]);

  /* Glissement vers le bas : un seul doigt, et seulement depuis le haut du contenu
     (feuille défilée, le geste la fait remonter au lieu de la fermer). */
  const onSheetTouchStart = (event: TouchEvent<HTMLDivElement>): void => {
    const touch = event.touches[0];
    const scrolled = (sheetRef.current?.scrollTop ?? 0) > 0;
    swipeRef.current = event.touches.length === 1 && touch && !scrolled ? { x: touch.clientX, y: touch.clientY } : null;
  };
  const onSheetTouchEnd = (event: TouchEvent<HTMLDivElement>): void => {
    const start = swipeRef.current;
    swipeRef.current = null;
    const touch = event.changedTouches[0];
    if (!start || !touch) return;
    const dy = touch.clientY - start.y;
    if (dy >= SHEET_SWIPE_CLOSE_THRESHOLD_PX && dy > Math.abs(touch.clientX - start.x)) setDetailOpen(false);
  };
  const onSheetTouchCancel = (): void => {
    swipeRef.current = null;
  };

  const countLabel = `${count} ${count > 1 ? labels.item_plural : labels.item_singular}`;

  return (
    <div className="bt-step-footer" ref={footerRef}>
      {detailOpen && count > 0 && (
        <div
          className="bt-footer-sheet"
          id={sheetId}
          ref={sheetRef}
          onTouchStart={onSheetTouchStart}
          onTouchEnd={onSheetTouchEnd}
          onTouchCancel={onSheetTouchCancel}
        >
          <span className="bt-footer-sheet__handle" aria-hidden="true" />
          <div className="bt-footer-sheet__header">
            <span className="bt-footer-sheet__title">{sheetTitle}</span>
            <button type="button" className="bt-footer-sheet__close" onClick={closeDetail} aria-label={labels.close}>
              ×
            </button>
          </div>
          {sheet}
        </div>
      )}

      <div className="bt-step-footer__row">
        {onBack && (
          <button type="button" className="bt-step-footer__back" onClick={onBack} aria-label={labels.back}>
            <span aria-hidden="true">←</span>
          </button>
        )}

        {count > 0 ? (
          <button
            type="button"
            className="bt-step-footer__info"
            onClick={(): void => setDetailOpen((open): boolean => !open)}
            aria-expanded={detailOpen}
            aria-controls={sheetId}
            ref={toggleRef}
          >
            {/* Pastille compteur : rend le panier non vide visible d'un coup d'œil. `key` la remonte
                à chaque changement de quantité pour rejouer son animation. */}
            <span className="bt-step-footer__badge" key={count} aria-hidden="true">
              {count}
            </span>
            <span className="bt-step-footer__info-text">
              <span className="bt-step-footer__total">
                {totalStruck && <s className="bt-step-footer__total-struck">{totalStruck}</s>}
                {totalValue}
              </span>
              <span className="bt-step-footer__count">
                {countLabel}
                <span className="bt-step-footer__detail-hint">
                  {' · '}
                  {detailOpen ? labels.hide_detail : labels.show_detail}
                </span>
              </span>
            </span>
            <span className="bt-step-footer__chevron" aria-hidden="true">
              {detailOpen ? '▴' : '▾'}
            </span>
          </button>
        ) : (
          <div className="bt-step-footer__spacer" />
        )}

        {primary && (
          <button
            type="button"
            className="bt-cta bt-cta--footer"
            onClick={primary.onClick}
            disabled={!!primary.disabled || !!primary.loading}
          >
            {primary.loading ? <span className="bt-spinner bt-spinner--inline" /> : primary.label}
          </button>
        )}
      </div>
    </div>
  );
}
